import React, { ChangeEvent, useCallback, useContext, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { GridRowParams } from '@material-ui/data-grid';
import FormDefault from '~/components/FormDefault';
import Loja from '~/components/Loja';
import { BuscaProduto, InputPercent } from '~/components/NovosInputs';
import Search from '~/components/Search';
import ToggleDefault from '~/components/ToggleDefault';
import { LojaContext } from '~/context/loja';
import api from '~/services/api';
import { formatCurrencyAsText, transformAsCurrency } from '~/utils/functions';
import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Container } from './styles';
import { schema } from './validations';
import DefaultLoader from '~/components/DefaultLoader';

const AjusteMargemProduto: React.FC = () => {
  const {
    register,
    setValue,
    control,
    getValues,
    reset,
    setFocus,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
  });
  const { loja } = useContext(LojaContext);
  const [loader, setLoader] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(true);
  const [lojaProduto, setLojaProduto] = useState<number>(loja.cod_loja);
  const [disableInputs, setDisableInputs] = useState<{
    produto: boolean;
    loja: boolean;
  }>({
    produto: false,
    loja: false,
  });
  const [flgPrioritaria, setFlgPrioritaria] = useState<boolean>(false);

  /**
   * Reseta form para criação de novo registro
   */
  const resetFormData = useCallback(() => {
    setFlgPrioritaria(false);
    setDisableInputs({
      produto: false,
      loja: false,
    });
    setTimeout(() => {
      reset({
        cod_loja: loja.cod_loja,
        cod_produto: { label: undefined, value: undefined },
        margem: '',
      });
    }, 150);
    setLojaProduto(loja.cod_loja);
  }, [reset, loja]);

  const onRowClick = useCallback(
    async (param: GridRowParams) => {
      setUpdate(true);
      const { row } = param;
      const {
        cod_produto,
        des_produto,
        val_margem,
        cod_loja,
        flg_prioritaria,
      } = row;
      setLojaProduto(cod_loja);

      setTimeout(() => {
        reset({
          cod_produto: {
            label: `${cod_produto} - ${des_produto}`,
            value: cod_produto,
          },
          cod_loja,
          margem: formatCurrencyAsText(val_margem),
        });
        setFlgPrioritaria(flg_prioritaria);
        setFocus('margem');
      }, 150);

      setLoader(false);
      setShowSearch(false);
      setDisableInputs({
        produto: true,
        loja: true,
      });
    },
    [reset, setFocus],
  );

  const onSave = handleSubmit(async () => {
    const { margem, cod_produto, cod_loja } = getValues();
    const margemTransformed = transformAsCurrency(margem);

    if (
      loja.regra_empresa &&
      margemTransformed > 99.99 &&
      loja.regra_empresa.tipo_margem > 0 &&
      loja.regra_empresa.tipo_margem < 4
    ) {
      return toast.warning(
        <>
          <div>Margem de referência informada é maior que 99,99%</div>
          <div style={{ marginTop: '0.3125rem' }}>
            Não permitida para o tipo de margem parametrizado na regra de
            empresa.
          </div>
        </>,
      );
    }

    const { data } = await api.post('/produto-margem', {
      cod_produto: cod_produto.value,
      cod_loja,
      cod_preco: 1,
      val_margem: margemTransformed,
      flg_prioritaria: flgPrioritaria,
    });

    if (data.success) {
      toast.success(data.message);
      resetFormData();
      setShowSearch(isUpdate);
      setUpdate(false);
    } else {
      toast.error(data.message);
    }

    setLoader(false);
  });

  const handleDelete = async () => {
    toast.error('Função Desabilitada');
  };

  const onNew = async () => {
    resetFormData();
    setShowSearch(false);
    setUpdate(false);
  };

  const onCancel = () => {
    setShowSearch(true);
    setUpdate(false);
    resetFormData();
  };

  const handleChangeReferencia = useCallback(
    (val: string) => {
      let ValueConverted;
      if (val.includes(',')) {
        const convertedValue = val.replace(',', '');
        const convertedValueString = convertedValue.slice(-2);
        ValueConverted = `${convertedValue.slice(
          0,
          -2,
        )},${convertedValueString}`;
      } else {
        ValueConverted = `00,0${val}`;
      }

      setValue('margem', ValueConverted.replace(',', '.'));
    },
    [setValue],
  );

  const getMargemInfo = async (cod_produto: number, cod_loja: number) => {
    if (cod_produto) {
      try {
        const { data } = await api.get('/produto-margem/busca-margem-produto', {
          params: {
            cod_produto,
            cod_loja,
          },
        });

        if (String(data.data[0].val_margem).includes('.')) {
          setValue('margem', formatCurrencyAsText(data.data[0].val_margem));
        } else {
          setValue('margem', formatCurrencyAsText(data.data[0].val_margem));
        }
        setFlgPrioritaria(data.data[0].flg_prioritaria);
      } catch (e) {
        setLoader(false);
        setValue('margem', '0,00');
      }
    }
  };

  const handlePrioritaria = useCallback(() => {
    setFlgPrioritaria(!flgPrioritaria);
  }, [flgPrioritaria]);

  if (loader) {
    return <DefaultLoader />;
  }

  return (
    <Container>
      {showSearch && (
        <Search newData={onNew} onRowClick={onRowClick} codTela={94} />
      )}
      {!showSearch && (
        <FormDefault
          codTela={94}
          codigoRegistro={[
            {
              value: watch('cod_produto')?.value,
              des_campo: 'Código do Registro',
            },
          ]}
          title="Ajuste de Margem por Produto"
          onSave={async () => {
            await onSave();
          }}
          onCancel={onCancel}
          isUpdate={isUpdate}
          onNew={onNew}
          onDelete={handleDelete}
          onClearFields={resetFormData}
          onReturnSearch={() => {
            setShowSearch(true);
          }}
          isDelete={false}
        >
          <Row>
            <Col md={12} sm={12}>
              <Loja
                selectedLoja={lojaProduto}
                IsInvalid={false}
                onChange={(e: number) => {
                  setValue('cod_loja', e);
                  setLojaProduto(e);
                  setValue('cod_produto', {
                    label: 'Selecione...',
                    value: undefined,
                  });

                  setValue('margem', '');
                  setFlgPrioritaria(false);
                }}
                isDisabled={disableInputs.loja}
              />
            </Col>
            <Col md={12} sm={12}>
              <BuscaProduto
                id="cod_produto"
                label="Buscar Produto"
                name="cod_produto"
                register={register}
                control={control}
                placeholder="Selecione..."
                disabled={disableInputs.produto}
                isError={!!errors.cod_produto}
                buscaNasLojas={lojaProduto}
                customOptions={{
                  hideBuscarPor: true,
                  buscarPor: ['Produto'],
                  showSelecionarItensContendo: false,
                }}
                getProduct={async (selected) => {
                  const selectedAny: any = selected;
                  setValue('cod_produto', {
                    label: `${selectedAny[0].cod_produto} - ${selectedAny[0].des_produto}`,
                    value: selectedAny[0].cod_produto,
                  });
                  await getMargemInfo(selectedAny[0].cod_produto, lojaProduto);
                  setFocus('margem');
                }}
                setValue={setValue}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12} md={2} style={{ minWidth: '180px' }}>
              <InputPercent
                label="Margem Referência"
                placeholder="0,00"
                min={-99999999}
                max={999999999}
                onInput={(event: ChangeEvent<HTMLInputElement>) =>
                  handleChangeReferencia(event.target.value)
                }
                name="margem"
                register={register}
                control={control}
                isError={false}
              />
            </Col>
            <Col sm={12} md={2}>
              <ToggleDefault
                labelText="Prioritária?"
                setChecked={flgPrioritaria}
                onSwitch={handlePrioritaria}
              />
            </Col>
          </Row>
        </FormDefault>
      )}
    </Container>
  );
};

export default AjusteMargemProduto;
